<template>
  <ModuleBase gray>
    <ChangePassword
      v-if="showPasswordModal"
      @close="showPasswordModal = false"
    />
    <PasswordConfirmationModal
      v-if="showConfirmationModal"
      :loading="isLoading"
      :invalidPassword="invalidPassword"
      @close="() => {
        showConfirmationModal = false;
        invalidPassword = false;
        isLoading = false;
      }"
      @submit="save"
    />
    <div :class="$style.base">
      <BaseHeading
        size="l"
        mb
      >
        {{ $t('account.heading') }}
      </BaseHeading>

      <BaseText :mb="2">{{ $t('account.description') }}</BaseText>

      <BaseForm @submit="submit">
        <BaseCard
          :heading="$t('account.login_info')"
          mb
        >
          <BaseInput
            v-model="firstName"
            :label="$t('global.first_name')"
            required
            mb
            v-test="'account-first-name'"
          />
          <BaseInput
            v-model="lastName"
            :label="$t('global.last_name')"
            mb
            v-test="'account-last-name'"
          />
          <BaseInput
            v-model="email"
            :label="$t('global.email')"
            required
            :error="
              isEmailUsed
                ? filters.capitalize(
                    $t('global.validations.in_use', {
                      value: $t('global.email')
                    })
                  )
                : ''
            "
            type="email"
            v-test="'account-email'"
          />
          <BaseButton
            color="inverted"
            mt
            @click="showPasswordModal = true"
            v-test="'account-btn-password'"
          >
            {{ $t('account.change_password') }}
          </BaseButton>
        </BaseCard>

        <BaseSpinner
          v-if="loading"
          inline
        />
        <TwoFactorAuthentication
          v-else
          :is2faActive="isTwoFactorEnabled"
          v-test="'two-factor-authentication'"
        />
        <BaseCard
          :heading="$t('admin.general_settings.form.language')"
          mb
          v-test="'account-interface'"
          v-highlight="'account-interface'"
        >
          <BaseText mb>
            {{ $t('admin.general_settings.form.language_info') }}
          </BaseText>
          <BaseDropdown
            v-model="language"
            :label="$t('admin.general_settings.form.user_language')"
            :options="languageOptions"
            :info="$t('global.language_auto_translated_info')"
            v-test="'account-language'"
          />
        </BaseCard>
        <BaseCard
          v-if="
            hasFeatureFlag('module-appointments') &&
            unleash.isEnabled('PushNotifications')
          "
          :heading="filters.capitalize($t('global.items.push_notification', 2))"
          mb
          v-test="'account-push-notifications'"
        >
          <BaseRadio
            v-model="numberOfDays"
            :label="$t('account.new_appointment_notification')"
            :options="[
              { value: 1, label: $t('global.today') },
              { value: 7, label: $t('global.next_x_days', { amount: 7 }) },
              { value: 30, label: $t('global.next_x_days', { amount: 30 }) },
              { value: 0, label: $t('account.i_dont_want_notifications') }
            ]"
            v-test="'account-push-notification-new-appointment'"
          />
        </BaseCard>
        <BaseCard
          :heading="filters.capitalize($t('global.items.notification', 2))"
          mb
        >
          <BaseCheckbox
            v-model="updates"
            :label="$t('account.updates')"
            v-test="'account-updates'"
          />
          <BaseCheckbox
            v-if="hasFeatureFlag('module-reports')"
            v-model="dayReports"
            :label="$t('account.day_reports')"
            mt
            v-test="'account-day-reports'"
          />
        </BaseCard>
        <div :class="$style.buttonWrap">
          <BaseButton
            :loading="isLoading"
            submitForm
            v-test="'account-submit'"
          >
            {{ $t('global.actions.save') }}
          </BaseButton>
        </div>
      </BaseForm>
    </div>
  </ModuleBase>
</template>

<script lang="ts">
import { useQuery } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { mapState } from 'pinia';
import { computed, defineComponent } from 'vue';
import config from '@/config';
import filters from '@/filters';
import { logValidationError } from '@/helpers/datadog';
import {
  NativeAppMessage,
  sendNativeAppMessage,
} from '@/helpers/native-app-bridge';
import { flash } from '@/helpers/ui';
import PasswordConfirmationModal from '@/modules/_shared/PasswordConfirmationModal.vue';
import ModuleBase from '@/modules/ModuleBase.vue';
import { useLocalizationStore } from '@/stores/localization';
import { useUserStore } from '@/stores/user';
import { CompanyLanguage } from '@/types';
import unleash from '@/unleash';
import ChangePassword from './ChangePassword.vue';
import TwoFactorAuthentication from './TwoFactorAuthentication.vue';

export default defineComponent({
  components: {
    ModuleBase,
    ChangePassword,
    TwoFactorAuthentication,
    PasswordConfirmationModal,
  },
  setup() {
    const {
      result,
      loading,
      refetch: refetchUser,
    } = useQuery(gql`
      query getCurrentUser {
        currentUser {
          twoFactorSettings {
            status
          }
        }
      }
    `);

    const isTwoFactorEnabled = computed(
      () => result.value?.currentUser.twoFactorSettings?.status === 'ENABLED',
    );

    return {
      unleash,
      filters,
      loading,
      isTwoFactorEnabled,
      refetchUser,
    };
  },
  data() {
    const { user } = useUserStore();
    return {
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      dayReports: user.settings.notifications.dayReports,
      updates: user.settings.notifications.updates,
      numberOfDays: user.settings.pushNotifications.newAppointmentInDays,
      admin: user.admin,
      isLoading: false,
      isEmailUsed: false,
      showPasswordModal: false,
      language: user?.settings.custom.language || null,
      showConfirmationModal: false,
      invalidPassword: false,
    };
  },
  computed: {
    ...mapState(useUserStore, ['hasFeatureFlag']),
    languageOptions() {
      const { languageLabels } = useLocalizationStore();

      const companyLanguageOptions = Object.keys(CompanyLanguage).map(
        (key: string) => ({
          value: key.toUpperCase(),
          label:
            languageLabels[key.toLowerCase() as keyof typeof languageLabels],
          labelExtra: config.localesUnverified.includes(
            key.toLowerCase() as (typeof config.localesUnverified)[number],
          )
            ? this.$t('global.language_auto_translated')
            : '',
        }),
      );

      return [
        { value: null, label: this.$t('account.use_company_language') },
        ...companyLanguageOptions,
      ];
    },
  },
  methods: {
    submit() {
      const { user } = useUserStore();
      if (this.email !== user.email) {
        this.showConfirmationModal = true;
      } else {
        this.save();
      }
    },
    save(password?: string) {
      this.isLoading = true;
      this.invalidPassword = false;

      const { updateUser } = useUserStore();

      const input = {
        firstName: this.firstName,
        lastName: this.lastName,
        email: this.email,
        settings: {
          custom: {
            language: this.language,
          },
          pushNotifications: {
            newAppointmentInDays: this.numberOfDays,
          },
          notifications: {
            dayReports: this.dayReports,
            updates: this.updates,
          },
        },
      };

      if (password) {
        input.currentPassword = password;
      }

      updateUser(input)
        .then(() => {
          flash(this.$t('global.flash.account_updated'));
          this.isEmailUsed = false;
          this.showConfirmationModal = false;
        })
        .catch((errors) => {
          this.isEmailUsed = errors?.includes('email') || false;
          this.invalidPassword = errors?.includes('current_password') || false;
          if (this.isEmailUsed) {
            this.showConfirmationModal = false;
          }
          if (!this.isEmailUsed && !this.invalidPassword) {
            logValidationError('updateUser', errors, input);
          }
        })
        .finally(() => {
          this.isLoading = false;
          if (this.numberOfDays > 0) {
            sendNativeAppMessage({
              message: NativeAppMessage.EnablePushNotification,
            });
          }
        });
    },
  },
  beforeRouteUpdate(to) {
    if (to.name === 'account') {
      // When closing the modal and going back to the main route, update the 2fa state by refetching the user
      this.refetchUser();
    }
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  max-width: 800px;
  margin: 0 auto;
}

.buttonWrap {
  display: flex;
  justify-content: flex-end;
}
</style>
